/**
 * SessionList component
 *
 * Scrollable list of user's chat sessions with infinite scroll.
 */

import { useRef, useCallback } from 'react';
import type { Session } from '../../types';
import { SessionItem } from './SessionItem';

interface SessionListProps {
  sessions: Session[];
  currentSessionUuid: string | null;
  onSelectSession: (uuid: string) => void;
  onDeleteSession: (uuid: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

export function SessionList({
  sessions,
  currentSessionUuid,
  onSelectSession,
  onDeleteSession,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
}: SessionListProps) {
  const listRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (!listRef.current || !onLoadMore || !hasMore || isLoadingMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    // Load more when user scrolls to within 100px of bottom
    if (scrollHeight - scrollTop - clientHeight < 100) {
      onLoadMore();
    }
  }, [onLoadMore, hasMore, isLoadingMore]);

  if (sessions.length === 0) {
    return (
      <div className="sessions-list" data-testid="sessions-list">
        <div className="empty-sessions">
          <p>Nema sesija</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={listRef}
      className="sessions-list"
      data-testid="sessions-list"
      onScroll={handleScroll}
    >
      {sessions.map((session) => (
        <SessionItem
          key={session.session_uuid}
          session={session}
          isActive={session.session_uuid === currentSessionUuid}
          onSelect={onSelectSession}
          onDelete={onDeleteSession}
        />
      ))}
      {isLoadingMore && (
        <div className="sessions-loading-more">
          <span>Učitavanje...</span>
        </div>
      )}
    </div>
  );
}

export default SessionList;
